import parseHtmlText from 'html-react-parser'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import translationKeys from '@translationKeys'

import { ACTION_TYPE_TO_STATUS_COLORS_MAP, ACTION_TYPE_TO_STATUS_LABEL_MAP } from '@modules/contacts/constants'
import { MessageStreamItem } from '@modules/contacts/types'
import { ProfileType } from '@modules/search/types'
import { Box } from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'

import {
  MessageAvatar,
  MessageBox,
  MessageBoxText,
  MessageHeader,
  MessageHeaderAuthor,
  MessageSenderInitials,
  MessageWrapper,
  NotificationStatus,
  Root,
} from './MessageStreamList.styles'

export interface MessageStreamListProps {
  messages: MessageStreamItem[]
  className?: string
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
}

const MessageStreamList = ({ className, hasNextPage, isFetchingNextPage, messages, fetchNextPage }: MessageStreamListProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: hasNextPage ? messages.length + 1 : messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 300,
    overscan: 3,
  })

  const virtualItems = virtualizer.getVirtualItems()

  useEffect(() => {
    const [lastItem] = [...virtualizer.getVirtualItems()].reverse()

    if (!lastItem) {
      return
    }

    if (lastItem.index >= messages.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.()
    }
    // eslint-disable-next-line
  }, [hasNextPage, fetchNextPage, messages.length, isFetchingNextPage, virtualizer.getVirtualItems()])

  const goToDelegateProfile = (id?: number, isHidden?: boolean) => {
    if (!id || isHidden) {
      return
    }

    navigate(`/plus/profile/${id}/${ProfileType.delegate}`)
  }

  return (
    <Root data-testid="message-stream-list">
      <Box
        ref={parentRef}
        sx={{
          height: '100%',
          width: 'calc(100% + 16px * 2)',
          overflow: 'auto',
          contain: 'strict',
          margin: '0 -16px',
          padding: '0 16px',
        }}
        className={className}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: virtualizer.getTotalSize(),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map(virtualItem => {
              const messageStreamItem = messages[virtualItem.index]

              if (!messageStreamItem) {
                return (
                  <Box key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement}>
                    Loading...
                  </Box>
                )
              }

              if (messageStreamItem.type === 'message') {
                const msg = messageStreamItem.item

                return (
                  <MessageWrapper key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement}>
                    <MessageHeader>
                      {msg.avatarSrc ? (
                        <MessageAvatar $isDisabled={msg.isHidden} avatarSrc={msg.avatarSrc} />
                      ) : (
                        <MessageSenderInitials $isDisabled={msg.isHidden}>
                          {msg.isAdmin ? t(translationKeys.ADMINISTRATOR)[0] : msg.authorInitials}
                        </MessageSenderInitials>
                      )}
                      <Box>
                        <Box display="flex">
                          <MessageHeaderAuthor
                            onClick={() => goToDelegateProfile(msg.authorId, msg.isHidden)}
                            $isDisabled={!msg.authorId || msg.isHidden}
                          >
                            {msg.isAdmin ? t(translationKeys.ADMINISTRATOR) : msg.author}:
                          </MessageHeaderAuthor>{' '}
                          {msg.date}
                        </Box>
                        <Box>{msg.companyName}</Box>
                      </Box>
                    </MessageHeader>
                    <MessageBox msg={msg}>
                      <MessageBoxText>{parseHtmlText(msg.messageText)}</MessageBoxText>
                    </MessageBox>
                  </MessageWrapper>
                )
              }

              const notificationStatus = messageStreamItem.item
              const { background, border } = ACTION_TYPE_TO_STATUS_COLORS_MAP[notificationStatus.status]

              return (
                <NotificationStatus
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  backgroundColor={background}
                  borderColor={border}
                >
                  <Box>{ACTION_TYPE_TO_STATUS_LABEL_MAP[notificationStatus.status]}</Box>
                  <Box>{notificationStatus.date}</Box>
                </NotificationStatus>
              )
            })}
          </Box>
        </Box>
      </Box>
    </Root>
  )
}

export default MessageStreamList
